import React from "react";
import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { baseUrl, liveRateUrl, optionCategoryUrl, optionExpiryUrl } from "../constant";
import { Link } from "react-router-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SPOT_TOKEN = "NIFTY-I";
const SPOT_ALT = "NIFTY";
const SPOT_LABEL = "Nifty Option Chain";

const SPOT_LINE_STYLE = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px 10px",
  fontWeight: 700,
  fontSize: 13,
  padding: "8px 10px",
  textAlign: "center",
};

const formatSpotChange = (val) => {
  const n = Number(val);
  if (Number.isNaN(n)) return "--";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}`;
};

const formatSpotPct = (val) => {
  const n = Number(val);
  if (Number.isNaN(n)) return "--";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

const computeSpotSlot = (price, rows) => {
  if (price == null || !rows?.length) return null;
  const p = Number(price);
  if (Number.isNaN(p)) return null;
  if (p < Number(rows[0].strike)) return -1;
  for (let i = 0; i < rows.length; i++) {
    const next = rows[i + 1];
    if (!next) return i;
    if (p >= Number(rows[i].strike) && p < Number(next.strike)) return i;
  }
  return null;
};

const pickSpotRow = (list) => {
  if (!Array.isArray(list)) return null;
  return (
    list.find((i) => i?.InstrumentIdentifier === SPOT_TOKEN) ||
    list.find((i) => i?.InstrumentIdentifier === SPOT_ALT) ||
    null
  );
};

// Spot bar shell: never re-renders — LTP/Chg updated via DOM refs only
const SpotLineShell = React.memo(
  function SpotLineShell({ label, ltpRef, chgRef }) {
    return (
      <div className="noftyoptionspotline" style={SPOT_LINE_STYLE}>
        <span>{label}</span>
        <span ref={ltpRef}>--</span>
        <span ref={chgRef}>Chg: -- (--)</span>
      </div>
    );
  },
  () => true,
);

const Mcxnfo = ({ nsedata }) => {
  const hasScrolled = useRef(false);
  const previousExpiry = useRef(null);
  const navigate = useNavigate();
  const rowRefs = useRef([]);
  const data1Ref = useRef([]);
  const spotQuoteRef = useRef(null);
  const spotSlotRef = useRef(null);
  const spotLtpEl = useRef(null);
  const spotChgEl = useRef(null);
  const spotReqId = useRef(0);
  const nsedataRef = useRef(nsedata);
  const previousPrices = useRef({});
  nsedataRef.current = nsedata;

  const [data1, setData1] = useState([]);
  const [spotSlot, setSpotSlot] = useState(null);
  const [rateFlash, setRateFlash] = useState({});
  const [expiry, setexpiry] = useState();
  const [listexp, setlisrexp] = useState();
  const [err, seterr] = useState();
  const [type, settype] = useState(0);
  const [nifty, setnifty] = useState(0);

  const getStrike = (id) => id.replace("CE", "").replace("PE", "");

  // 🔹 Only strike number (20600)
  const getStrikePrice = (id) => parseInt(id.match(/\d{5}/)?.[0]);

  // 🔹 Combine CE + PE
  const formatData = (apiData) => {
    const grouped = {};

    apiData.forEach((item) => {
      const key = getStrike(item.InstrumentIdentifier);

      if (!grouped[key]) {
        grouped[key] = {
          strike: getStrikePrice(item.InstrumentIdentifier),
          call: null,
          put: null,
        };
      }

      if (item.InstrumentIdentifier.endsWith("CE")) {
        grouped[key].call = item;
      } else {
        grouped[key].put = item;
      }
    });

    return Object.values(grouped).sort((a, b) => a.strike - b.strike);
  };
  // console.log(formatData(option));
  // 🔹 API Call
  const fetchData = async (exp = listexp) => {
    if (!exp) return;
    try {
      const token = localStorage.getItem("token");
      const formdata = new FormData();
      formdata.append("Token", token);
      formdata.append("type", 1);
      formdata.append("expiry", exp);
      const res = await axios.post(optionCategoryUrl, formdata);
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      setData1(rows);
    } catch (err) {
      console.log(err);
      setData1([]);
    }
  };

  const getexpiry = async () => {
    const token = localStorage.getItem("token");

    try {
      const formdata = new FormData();
      formdata.append("Token", token);
      formdata.append("type", "");

      const res = await axios.post(optionExpiryUrl, formdata);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setexpiry(list);
      if (list.length > 0) {
        setlisrexp(list[0]);
      }
    } catch (error) {
      seterr(error);
      console.log(error);
    }
  };

  // Load expiry list once
  useEffect(() => {
    getexpiry();
  }, []);

  // Expiry change / first select → fetch new chain + allow ATM scroll
  useEffect(() => {
    if (!listexp) return;
    hasScrolled.current = false;
    previousExpiry.current = listexp;
    // Reset spot once per expiry — no continuous live updates (avoids blink)
    spotQuoteRef.current = null;
    spotSlotRef.current = null;
    setSpotSlot(null);
    setData1([]);
    fetchData(listexp);
  }, [listexp]);

  // Live refresh for selected expiry
  useEffect(() => {
    if (!listexp) return;
    const id = setInterval(() => {
      fetchData(listexp);
    }, 2500);
    return () => clearInterval(id);
  }, [listexp]);

  data1Ref.current = data1;

  const chainRows = Array.isArray(data1)
    ? data1.filter((item) => item?.ce && item?.pe)
    : [];

  const writeSpotText = (row) => {
    if (!row) return;
    if (spotLtpEl.current) {
      spotLtpEl.current.textContent = String(row.LastTradePrice ?? "--");
    }
    if (spotChgEl.current) {
      spotChgEl.current.textContent = `Chg: ${formatSpotChange(
        row.PriceChange,
      )} (${formatSpotPct(row.PriceChangePercentage)})`;
    }
  };

  const updateSpotSlot = (ltp, rows) => {
    if (spotSlotRef.current != null) return; // keep slot fixed — no jump/blink
    const nextSlot = computeSpotSlot(ltp, rows);
    if (nextSlot == null) return;
    spotSlotRef.current = nextSlot;
    setSpotSlot(nextSlot);
  };

  // Set spot data ONCE only — do not keep updating (stops blink)
  const applySpot = (row) => {
    if (row?.LastTradePrice == null) return;
    if (spotQuoteRef.current) return;

    spotQuoteRef.current = {
      LastTradePrice: row.LastTradePrice,
      PriceChange: row.PriceChange,
      PriceChangePercentage: row.PriceChangePercentage,
    };
    writeSpotText(spotQuoteRef.current);

    const rows = data1Ref.current.filter((item) => item?.ce && item?.pe);
    updateSpotSlot(row.LastTradePrice, rows);
  };

  const fetchSpot = async () => {
    if (spotQuoteRef.current) return; // already set — skip
    const token = localStorage.getItem("token");
    if (!token) return;
    const reqId = ++spotReqId.current;
    try {
      const formSpecific = new FormData();
      formSpecific.append("Token", token);
      formSpecific.append("catToken", SPOT_TOKEN);
      const resSpecific = await axios.post(liveRateUrl, formSpecific);
      if (reqId !== spotReqId.current) return;

      let row = pickSpotRow(resSpecific?.data?.livedata);
      if (
        !row &&
        Array.isArray(resSpecific?.data?.livedata) &&
        resSpecific.data.livedata.length === 1 &&
        resSpecific.data.livedata[0]?.LastTradePrice != null
      ) {
        row = resSpecific.data.livedata[0];
      }

      if (!row) {
        const formAll = new FormData();
        formAll.append("Token", token);
        const resAll = await axios.post(liveRateUrl, formAll);
        if (reqId !== spotReqId.current) return;
        row = pickSpotRow(resAll?.data?.livedata);
      }

      if (row) {
        applySpot(row);
        return;
      }
    } catch (e) {
      // fall through
    }
    if (reqId !== spotReqId.current) return;
    applySpot(pickSpotRow(nsedataRef.current));
  };

  // Fetch spot once on mount (no interval)
  useEffect(() => {
    fetchSpot();
  }, []);

  // When chain arrives, place frozen spot once (no text re-write every refresh)
  useLayoutEffect(() => {
    data1Ref.current = data1;
    const rows = data1.filter((item) => item?.ce && item?.pe);
    if (!rows.length) return;

    if (!spotQuoteRef.current) {
      fetchSpot();
      return;
    }
    updateSpotSlot(spotQuoteRef.current.LastTradePrice, rows);
  }, [data1]);

  useLayoutEffect(() => {
    writeSpotText(spotQuoteRef.current);
  }, [spotSlot]);

  // CE/PE LTP flash — only when rate changes
  useEffect(() => {
    const rows = Array.isArray(data1)
      ? data1.filter((item) => item?.ce && item?.pe)
      : [];
    const changed = {};
    rows.forEach((item) => {
      [
        [item.ce.instrument_token, item.ce.ltp],
        [item.pe.instrument_token, item.pe.ltp],
      ].forEach(([token, ltp]) => {
        if (token == null || ltp == null) return;
        const prev = previousPrices.current[token];
        previousPrices.current[token] = ltp;
        if (prev === undefined || Number(prev) === Number(ltp)) return;
        changed[token] = Number(ltp) > Number(prev) ? "#22c55e" : "#ef4444";
      });
    });
    const keys = Object.keys(changed);
    if (!keys.length) return;

    setRateFlash((f) => ({ ...f, ...changed }));
    const timers = keys.map((token) =>
      setTimeout(() => {
        setRateFlash((f) => {
          if (f[token] !== changed[token]) return f;
          const next = { ...f };
          delete next[token];
          return next;
        });
      }, 450),
    );
    return () => timers.forEach(clearTimeout);
  }, [data1]);

  const fixnifty = () => {
    const fixedRate = spotQuoteRef.current?.LastTradePrice;
    const rows = data1.filter((item) => item?.ce && item?.pe);
    if (!fixedRate || !rows?.length) return;

    let nearestIndex = 0;
    let minDiff = Math.abs(rows[0]?.strike - fixedRate);

    rows.forEach((item, index) => {
      const diff = Math.abs(item?.strike - fixedRate);
      if (diff < minDiff) {
        minDiff = diff;
        nearestIndex = index;
      }
    });

    const tryScroll = (attempts = 0) => {
      const el = rowRefs.current[nearestIndex];
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        hasScrolled.current = true;
        return;
      }
      if (attempts < 15) {
        setTimeout(() => tryScroll(attempts + 1), 80);
      }
    };

    tryScroll();
  };

  // After new option data renders, scroll to ATM / spot strike line
  useEffect(() => {
    if (!listexp || !data1?.length || !spotQuoteRef.current?.LastTradePrice)
      return;
    if (hasScrolled.current) return;

    const t = setTimeout(() => {
      fixnifty();
    }, 200);

    return () => clearTimeout(t);
  }, [data1, listexp]);

  // const getBuyColor = ({token, currentPrice}) => {

  //   const previousBuyPrice = localStorage.getItem(token + "-Buy-Price");

  //   if (previousBuyPrice > currentPrice) {
  //     localStorage.setItem(token + "-Buy-Price", currentPrice);
  //     console.log(previousBuyPrice, currentPrice, token, "red");
  //     return { backgroundColor: "red" };
  //   } else if (previousBuyPrice < currentPrice) {
  //     console.log(previousBuyPrice, currentPrice, token, "green");
  //     localStorage.setItem(token + "-Buy-Price", currentPrice);
  //     return { backgroundColor: "green" };
  //   } else if (previousBuyPrice === currentPrice) {
  //     localStorage.setItem(token + "-Buy-Price", currentPrice);
  //     const themelight = localStorage.getItem("background");
  //     if (themelight == 0) {
  //       return { backgroundColor: "#1e293b" };
  //     } else if (themelight == 1) {
  //       return { backgroundColor: "#edf9fe" };
  //     } else if (themelight == 2) {
  //       return { backgroundColor: "#1e5655" };
  //     }
  //   }
  // };

  const handleChange = (e) => {
    setlisrexp(e.target.value);
  };

  const onbuttonclick = async ({ token, title, catid, exparydate }) => {
    await navigate("/niftyoptionmarkettrade", {
      state: {
        name: title,
        token: token,
        catid: catid,
        exparydate: exparydate,
      },
    });
  };

  const getTheme = () => {
    const stored = localStorage.getItem("background") || "light";
    const legacy = { 0: "dark", 1: "light", 2: "green" };
    const key = legacy[stored] || stored;
    if (key === "dark") {
      const bg = "#1e293b";
      return {
        pageBg: bg,
        headerBg: bg,
        rowBg: bg,
        selectBg: bg,
        selectBorder: "#cbd5e1",
        border: "#334155",
        text: "#ffffff",
        muted: "#94a3b8",
        strikeBg: bg,
        belowSpotBg: bg,
        aboveSpotBg: bg,
        spotBg: "#f59e0b",
        spotText: "#1a1a1a",
        chevron: "#cbd5e1",
      };
    }
    if (key === "green") {
      const bg = "#1e5655";
      return {
        pageBg: bg,
        headerBg: bg,
        rowBg: bg,
        selectBg: bg,
        selectBorder: "#efda95",
        border: "#36474f",
        text: "#ffffff",
        muted: "#b28e4d",
        strikeBg: bg,
        belowSpotBg: bg,
        aboveSpotBg: bg,
        spotBg: "#ff7043",
        spotText: "#ffffff",
        chevron: "#efda95",
      };
    }
    const bg = "#edf9fe";
    return {
      pageBg: bg,
      headerBg: bg,
      rowBg: bg,
      selectBg: bg,
      selectBorder: "#064a3c",
      border: "#d7e8f0",
      text: "#111111",
      muted: "#757575",
      strikeBg: bg,
      belowSpotBg: bg,
      aboveSpotBg: bg,
      spotBg: "#e65100",
      spotText: "#ffffff",
      chevron: "#333333",
    };
  };

  const theme = getTheme();

  const formatPct = (pcp) => {
    const n = Number(pcp);
    if (Number.isNaN(n)) return `${pcp}%`;
    const sign = n > 0 ? "+" : "";
    return `${sign}${n}%`;
  };

  const pctChipStyle = (pcp) => {
    const n = Number(pcp);
    const up = n >= 0;
    return {
      display: "inline-block",
      marginTop: 4,
      padding: "2px 8px",
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      color: "#fff",
      backgroundColor: up ? "#2e7d32" : "#d32f2f",
      lineHeight: 1.4,
    };
  };

  return (
    <>
      <div style={{ background: theme.pageBg }}>
        {/* Sticky expiry + column headers — stay fixed while list scrolls */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            background: theme.pageBg,
          }}
        >
          <div style={{ padding: "12px 16px 8px" }}>
            <div style={{ position: "relative", width: "100%" }}>
              <select
                value={listexp}
                onChange={handleChange}
                style={{
                  width: "100%",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                    border: `2px solid ${theme.selectBorder}`,
                    borderRadius: 6,
                    background: theme.selectBg,
                  padding: "12px 36px 12px 14px",
                  fontSize: 15,
                  fontWeight: 500,
                  color: theme.text,
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                {expiry ? (
                  expiry.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))
                ) : (
                  <option>{err || "Loading..."}</option>
                )}
              </select>
              <span
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: theme.chevron,
                  fontSize: 12,
                  lineHeight: 1,
                }}
              >
                ▼
              </span>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px 0",
              borderBottom: `1px solid ${theme.border}`,
              background: theme.headerBg,
            }}
          >
            <div
              style={{
                flex: 1,
                textAlign: "center",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.6,
                color: theme.muted,
                textTransform: "uppercase",
              }}
            >
              Call LTP
            </div>
            <div
              style={{
                width: "28%",
                textAlign: "center",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.6,
                color: theme.muted,
                textTransform: "uppercase",
              }}
            >
              Strike
            </div>
            <div
              style={{
                flex: 1,
                textAlign: "center",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.6,
                color: theme.muted,
                textTransform: "uppercase",
              }}
            >
              Put LTP        

            </div>
          </div>
        </div>

        <div
          className="tab-pane fade show active"
          id="home-tab-search" 
          role="tabpanel"
          aria-labelledby="home-search"
        >
            <div
              className="tab_search_body"
              style={{ padding: 0, background: theme.pageBg }}
            >
            {!chainRows?.length ? (
              <div
                style={{ 
                  
                  padding: 24,
                  textAlign: "center",
                  color: theme.muted, 
                  fontSize: 14,
                }}
              >
                {listexp ? "Loading option chain..." : "Loading expiry..."}
              </div>
            ) : null}
            {chainRows.map((item, index) => {
              const sideBg = theme.pageBg;
              const ceFlash = rateFlash[item.ce.instrument_token];
              const peFlash = rateFlash[item.pe.instrument_token];

              return (
                <React.Fragment key={item.strike ?? index}>
                  {spotSlot === -1 && index === 0 ? (
                    <SpotLineShell
                      label={SPOT_LABEL}
                      ltpRef={spotLtpEl}
                      chgRef={spotChgEl}
                    />
                  ) : null}
                  <div
                    ref={(el) => (rowRefs.current[index] = el)}
                    style={{
                      display: "flex",
                      alignItems: "stretch",
                      borderBottom: `1px solid ${theme.border}`,
                      minHeight: 64,
                      background: theme.rowBg,
                      scrollMarginTop: 120,
                    }}
                  >
                    {/* CALL */}
                    <div
                      onClick={() => {
                        onbuttonclick({
                          title: item.ce.title,
                          catid: item.ce.id,
                          exparydate: item.ce.expire_date,
                          token: item.ce.instrument_token,
                        });
                      }}
                      style={{ 
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px 6px",
                        background: sideBg,
                        borderRight: `1px solid ${theme.border}`,
                        cursor: "pointer",
                      }}
                    >
                      <Link
                        to=""
                        style={{
                          textDecoration: "none",
                          textAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: ceFlash || theme.text,
                            lineHeight: 1.2,
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: "transparent",
                            transition: "color 0.15s ease",
                          }}
                        >
                          {item.ce.ltp}
                        </div>
                        <span style={pctChipStyle(item.ce.pcp)}>
                          {formatPct(item.ce.pcp)}
                        </span>
                      </Link>
                    </div>

                    {/* STRIKE */}
                    <div
                      style={{
                        width: "28%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: theme.strikeBg,
                        borderRight: `1px solid ${theme.border}`,
                        padding: "10px 4px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: theme.text,
                        }}
                      >
                        {item.strike}
                      </div>
                    </div>

                    {/* PUT */}
                    <div
                      onClick={() => {
                        onbuttonclick({
                          title: item.pe.title,
                          catid: item.pe.id,
                          exparydate: item.pe.expire_date,
                          token: item.pe.instrument_token,
                        });
                      }}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px 6px",
                        background: sideBg,
                        cursor: "pointer",
                      }}
                    >
                      <Link
                        to=""
                        style={{
                          textDecoration: "none",
                          textAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: peFlash || theme.text,
                            lineHeight: 1.2,
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: "transparent",
                            transition: "color 0.15s ease",
                          }}
                        >
                          {item.pe.ltp}
                        </div>
                        <span style={pctChipStyle(item.pe.pcp)}>
                          {formatPct(item.pe.pcp)}
                        </span>
                      </Link>
                    </div>
                  </div>
                  {spotSlot === index ? (
                    <SpotLineShell
                      label={SPOT_LABEL}
                      ltpRef={spotLtpEl}
                      chgRef={spotChgEl}
                    />
                  ) : null}
                </React.Fragment>
              );
            })}
          </div>

          <div
            className="tab-pane fade"
            id="others-tabs"
            role="tabpanel"
            aria-labelledby="others"
          >
            <div className="tab_search_body"></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Mcxnfo;
